// ThemeSync — handles dark mode for the Seiva UI across three inputs:
//   1. User preference stored in localStorage.seiva_theme ("light" | "dark" | "auto")
//   2. System preference via (prefers-color-scheme: dark) — when mode === "auto"
//   3. Host site postMessage ({type: "seiva:theme", value: "light"|"dark"|"auto"}) — embed only
//   4. Host site postMessage ({type: "seiva:style-vars", vars: {"--accent-primary":"#..."}}) — embed only
//
// The module runs on import (no hook registration needed). It sets/unsets the
// `dark` class on <html> so every `html.dark` override in app.css applies.

const STORAGE_KEY = "seiva_theme"
const EMBED = window.location.pathname.startsWith("/embed")

function resolved(mode) {
  if (mode === "light" || mode === "dark") return mode
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function apply(mode) {
  const next = resolved(mode)
  document.documentElement.classList.toggle("dark", next === "dark")
}

function currentMode() {
  return localStorage.getItem(STORAGE_KEY) || "auto"
}

function setMode(mode) {
  if (!["light", "dark", "auto"].includes(mode)) return
  localStorage.setItem(STORAGE_KEY, mode)
  apply(mode)
}

function isAllowedCssVar(name) {
  return [
    "--n-",
    "--s-",
    "--accent-",
    "--bg-",
    "--text-",
    "--button-",
    "--modal-",
    "--border-",
    "--gradient-",
    "--surface-",
    "--status-",
    "--node-",
    "--field-",
    "--badge-",
    "--r-",
    "--radius-",
    "--shadow-",
    "--font-",
  ].some((prefix) => name.startsWith(prefix)) || [
    "--danger",
    "--warn",
    "--error",
    "--warning",
    "--success",
    "--info",
  ].includes(name)
}

// Reject CSS breakout / exfiltration vectors. `setProperty` already sandboxes a
// single custom-property value, but this is belt-and-suspenders for the embed
// postMessage path (a hostile host could push `red;}html{background:url(...)`).
// Legit values (hex, rgba(), color-mix(), linear-gradient(), shadow lists) pass.
function safeCssVarValue(value) {
  if (typeof value !== "string" || value.length === 0 || value.length > 240) return false
  if (/[;{}<>\\]/.test(value)) return false
  if (/url\(|expression\(|@import/i.test(value)) return false
  return true
}

function applyStyleVars(vars) {
  if (!vars || typeof vars !== "object" || Array.isArray(vars)) return

  Object.entries(vars).forEach(([name, value]) => {
    if (!isAllowedCssVar(name)) return
    if (!safeCssVarValue(value)) return
    document.documentElement.style.setProperty(name, value)
  })
}

// Initial apply — runs as early as possible (imported from app.js defer).
apply(currentMode())

// React to system-preference changes when mode is "auto".
const mq = window.matchMedia("(prefers-color-scheme: dark)")
mq.addEventListener("change", () => {
  if (currentMode() === "auto") apply("auto")
})

// Embed-only: accept postMessage from the host site.
// Origin validation happens server-side via EmbedOriginGuard + EmbedConfig.allowed_origins;
// we trust that by the time the iframe is rendered, any hostile origin is already blocked.
if (EMBED) {
  window.addEventListener("message", (event) => {
    if (!event.data) return

    if (event.data.type === "seiva:theme") {
      setMode(event.data.value)
    }

    if (event.data.type === "seiva:style-vars") {
      applyStyleVars(event.data.vars)
    }
  })
}

// Expose the setter for in-app toggles (topbar dark mode switch, etc.).
window.setSeivaTheme = setMode
